import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

// One card per uni channel; the template switches on channel.kind. Values arrive in
// hundredths (the wire convention) and are divided by 100 for display. Boiler and fan
// controls emit UNICOMMAND up to the dashboard; nothing is drawn optimistically — the
// card reflects telemetry, which is what confirms an asynchronous boiler command.
@Component({
  selector: 'app-uni-channel-card',
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule],
  templateUrl: './uni-channel-card.component.html',
  styleUrls: ['./uni-channel-card.component.scss'],
})
export class UniChannelCardComponent {
  @Input() channel: any;
  @Input() telemetry: any = {};      // { PROPERTY: value }
  @Input() canConfigure = false;

  @Output() command = new EventEmitter<{ channelId: number; cmd: string; value: number }>();

  // Translation keys; the template pipes levelText so a language switch repaints it.
  private static readonly LEVELS = [
    'uni.water.full', 'uni.water.half', 'uni.water.low', 'uni.water.empty', 'uni.water.error',
  ];

  get kind(): string { return this.channel?.kind; }
  get params(): any { return this.channel?.params || {}; }

  val(prop: string): number | undefined {
    const v = this.telemetry?.[prop];
    return v === undefined || v === null ? undefined : v;
  }
  has(prop: string): boolean { return this.val(prop) !== undefined; }

  // hundredths -> display number
  scaled(prop: string, digits = 1): string {
    const v = this.val(prop);
    return v === undefined ? '—' : (v / 100).toFixed(digits);
  }

  get levelText(): string {
    const v = this.val('LEVEL_CODE');
    return v === undefined ? 'common.none' : (UniChannelCardComponent.LEVELS[v] ?? 'uni.water.error');
  }

  get alarmBits(): number { return this.val('ALARM') || 0; }
  get hasAlarm(): boolean { return this.alarmBits !== 0; }

  get mode(): number { return this.val('MODE') ?? (this.params.modeDefault === 'manual' ? 1 : 0); }
  get isManual(): boolean { return this.mode === 1; }

  get on(): boolean { return this.val('ON_OFF') === 1; }
  get flame(): boolean { return this.val('FLAME') === 1; }

  // fan duty as a 0..100 slider value
  get dutyPct(): number {
    const v = this.val('FAN_DUTY');
    return v === undefined ? 0 : Math.round(v / 100);
  }

  get pressureText(): string {
    if (this.has('PRESSURE_BAR')) return this.scaled('PRESSURE_BAR', 2);
    if (this.has('PRESSURE_RAW')) return String(this.val('PRESSURE_RAW'));
    return '—';
  }

  /** Raw ADC counts carry no unit, so the suffix is only shown for a calibrated reading. */
  get pressureUnitKey(): string {
    return this.has('PRESSURE_BAR') ? 'uni.bar' : '';
  }

  // Boiler target: allowed values from config, current from telemetry.
  get allowed(): number[] { return this.params.allowed || []; }
  get targetC(): number {
    const v = this.val('TARGET_TEMP');
    return v === undefined ? (this.params.defaultTarget || 0) / 100 : Math.round(v / 100);
  }

  setTarget(v: any): void {
    this.command.emit({ channelId: this.channel.id, cmd: 'SET_TARGET', value: Number(v) * 100 });
  }
  togglePower(): void {
    this.command.emit({ channelId: this.channel.id, cmd: 'SET', value: this.on ? 0 : 1 });
  }
  setSwitch(on: boolean): void {
    this.command.emit({ channelId: this.channel.id, cmd: 'SET', value: on ? 1 : 0 });
  }
  setDuty(pct: any): void {
    this.command.emit({ channelId: this.channel.id, cmd: 'SET', value: Math.round(Number(pct) * 100) });
  }
  setMode(manual: boolean): void {
    this.command.emit({ channelId: this.channel.id, cmd: 'SET_MODE', value: manual ? 1 : 0 });
  }
}
